import videojs from 'video.js';

import './playcover.css';
import PlaycoverPlayButton from '../bundled/playcover/playcover-play-button.js'
import PlaycoverContentWrapper from '../bundled/playcover/playcover-content-wrapper.js'

const _poster = new WeakMap();
const _playButton = new WeakMap();
const _title = new WeakMap();

/**
 * The Playcover module.
 * @member {PlaycoverPlugin} playcover
 * @memberof MediasitePlayer#
 */

/**
 * Provides configuration options for the play button and poster image that display before media has been loaded.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class PlaycoverPlugin extends videojs.getPlugin('plugin') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} [options]
     */
    constructor(player, options) {
        super(player, options);

        player.ready(() => {
            const playcoverContentWrapper = new PlaycoverContentWrapper(player, { name: 'PlaycoverContentWrapper' });
            const playButton = new PlaycoverPlayButton(player, { name: 'PlaycoverPlayButton' });
            playcoverContentWrapper.addChild(playButton);

            const title = videojs.dom.createEl('h1', {
                className: 'mediasite-player__title-overlay',
                id: 'mediasite-player-title-overlay'
            });

            const poster = player.getChild('PosterImage');
            poster.addClass('mediasite-player__play-cover');
            poster.addClass('mediasite-player__play-cover--preloading');
            poster.addChild(playcoverContentWrapper);
            poster.el().appendChild(title);

            _poster.set(this, poster);
            _playButton.set(this, playButton);
            _title.set(this, title);

            player.one('playcoverloaded', () => {
                poster.removeClass('mediasite-player__play-cover--preloading');
            });
        });
    }

    /**
     * Sets the play cover custom content.
     * Passing null, undefined, '', or anything other than a videojs component, 
     * removes any existing custom content without replacing
     * @param {videojs.Component} vjsComponent
     * @example
     * player.playcover.addComponent(someCustomVideoJsComponent);
     */
    setPlaycoverCustomContent(vjsComponent, index) {
        const playcoverContentWrapper = this.player.getChild('PosterImage').getChild('PlaycoverContentWrapper');
        const children = playcoverContentWrapper.children();
        const playButton = _playButton.get(this);
        for (const child of children) {
            if (child !== playButton) {
                playcoverContentWrapper.removeChild(child);
            }
        }
        if (vjsComponent != null && vjsComponent !== '') {
            try {
                vjsComponent.addClass('mediasite-player__playcover-custom-content')
                playcoverContentWrapper.addChild(vjsComponent, null, index);
            } catch (e) {
                this.log(e)
            };
        }
    }

    /**
     * Adds the given component to the play cover.
     * @param {videojs.Component} vjsComponent 
     * @example
     * player.playcover.addComponent(someCustomVideoJsComponent);
     */
    addComponent(vjsComponent, index) {
        const poster = this.player.getChild('PosterImage');
        poster.addChild(vjsComponent, null, index);
    }

    /**
     * Removes the given component from the play cover if it exists.
     * @param {videojs.Component} vjsComponent 
     * @example
     * player.playcover.removeComponent(someCustomVideoJsComponent);
     */
    removeComponent(vjsComponent) {
        const poster = this.player.getChild('PosterImage');
        poster.removeChild(vjsComponent);
    }

    /**
     * Removes any custom components that have been added to the play cover.
     * @example
     * player.playcover.removeAllComponents();
     */
    removeAllComponents() {
        const poster = this.player.getChild('PosterImage');
        const playcoverContentWrapper = poster.getChild('PlaycoverContentWrapper');
        const children = poster.children();
        const title = _title.get(this);
        for (const child of children) {
            if (child !== title && child !== playcoverContentWrapper) {
                poster.removeChild(child);
            }
        }
        this.setPlaycoverCustomContent(null);
    }

    /**
     * A boolean indicating whether or not to show a play button.
     * @type {boolean}
     * @example
     * player.playcover.showPlayButton = true;
     */
    set showPlayButton(showPlayButton) {
        this.player.ready(() => {
            const bigPlayButton = _playButton.get(this);
            if (showPlayButton) {
                bigPlayButton.removeClass('vjs-hidden');
            }
            else {
                bigPlayButton.addClass('vjs-hidden');
            }
        });
    }

    get showPlayButton() {
        return !_playButton.get(this).hasClass('vjs-hidden');
    }

    /**
     * A boolean indicating whether or not the play cover is clickable.
     * @type {boolean}
     * @example
     * player.playcover.clickable = true;
     */
    set clickable(clickable) {
        this.#canClick = clickable;

        const bigPlayButton = _playButton.get(this);
        const poster = _poster.get(this);

        this.off(bigPlayButton, ['tap', 'click'], this.#handlePlayCoverClick);
        this.off(poster, ['tap', 'click'], this.#handlePlayCoverClick);
        poster.addClass('not-clickable');

        if (clickable) {
            this.on(bigPlayButton, ['tap', 'click'], this.#handlePlayCoverClick);
            this.on(poster, ['tap', 'click'], this.#handlePlayCoverClick);
            poster.removeClass('not-clickable');
        }
    }

    get clickable() {
        return this.#canClick;
    }

    /**
     * Sets the title text that is displayed on the play cover.<br><br>Note: this property gets automatically populated whenever {@link MetadataPlugin#title} is set.
     * @type {string}
     * @example
     * player.playcover.title = 'Title to be shown on the play cover';
     */
    set title(title) {
        this.player.ready(() => {
            _title.get(this).textContent = title;
        });
    }
    get title() {
        return _title.get(this).textContent;
    }

    /**
     * A boolean configuring whether or not the presentation title should be visible on the play cover.
     * @type {boolean}
     * @example
     * player.playcover.showTitle = true;
     */
    set showTitle(showTitle) {
        this.player.ready(() => {
            const title = _title.get(this);
            if (showTitle) {
                title.classList.remove('vjs-hidden');
            }
            else {
                title.classList.add('vjs-hidden');
            }
        });
    }
    get showTitle() {
        return !_title.get(this).classList.contains('vjs-hidden');
    }

    /**
     * A boolean indicating whether or not the play cover should be visible.
     * @type {boolean}
     * @example
     * player.playcover.visible = true;
     */
    set visible(visible) {
        this.player.ready(() => {
            const poster = _poster.get(this);

            if (visible) {
                poster.removeClass('vjs-hidden');
                this.clickable = this.clickable;
            }
            else {
                poster.addClass('vjs-hidden');
                this.player.addClass('vjs-has-started');
                this.clickable = false;
            }
        });
    }

    get visible() {
        const poster = _poster.get(this);
        return poster ? !poster.hasClass('vjs-hidden') : false;
    }

    /**
     * A boolean indicating whether or not the play cover should dim the poster image.
     * @type {boolean}
     * @example
     * player.playcover.dimmed = true;
     */
    set dimmed(dimmed) {
        this.player.ready(() => {
            const poster = _poster.get(this);
            if (dimmed) {
                poster.addClass('mediasite-player__play-cover--dimmed');
            }
            else {
                poster.removeClass('mediasite-player__play-cover--dimmed');
            }
        });
    }

    get dimmed() {
        return _poster.get(this).hasClass('mediasite-player__play-cover--dimmed');
    }

    #handlePlayCoverClick(event) {

        //Jump out if click was on an anchor tag in the play cover button
        if (event.target && event.target.tagName.toLowerCase() === 'a') return;
        if (event.target && event.target.id.toLowerCase() === 'reset-answers') return;

        this.player.loader().loadPresentation().then(() => this.visible = false);
        this.player.play();
        this.player.trigger('playcoverclick');
    }

    #canClick = true;

    dispose() {
        this.removeAllComponents();
        super.dispose();
    }
}

export default PlaycoverPlugin;
